import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyIcon, LockIcon, ShieldCheckIcon, SmartphoneIcon, FingerprintIcon, ArrowRightIcon, CloudIcon, HistoryIcon, ClockIcon, VideoIcon, BellIcon, ShieldAlertIcon, ClipboardCheckIcon, PenToolIcon, WrenchIcon, HeadphonesIcon, QrCodeIcon, UserPlusIcon, FileTextIcon, BellRingIcon, HelpCircleIcon, ChevronDownIcon, CheckCircle2Icon, ActivityIcon } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
const services = [{
  icon: ShieldCheckIcon,
  title: 'Badge Access Systems',
  description: 'Secure your premises with employee badge systems that track entry and restrict unauthorized access to sensitive areas.',
  color: 'text-indigo-500',
  bg: 'bg-indigo-50'
}, {
  icon: LockIcon,
  title: 'Door Entry Systems',
  description: 'Advanced intercom and remote door release systems to safely manage visitor entry from your front desk or mobile device.',
  color: 'text-rose-500',
  bg: 'bg-rose-50'
}, {
  icon: KeyIcon,
  title: 'Security Codes & Keypads',
  description: 'Pin-code access control for specific rooms or buildings, allowing easy revocation of access without changing physical locks.',
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}, {
  icon: SmartphoneIcon,
  title: 'Keycard & Mobile Access',
  description: 'Modernize your security with RFID keycards or mobile credentialing, allowing employees to unlock doors with their smartphones.',
  color: 'text-teal-500',
  bg: 'bg-teal-50'
}, {
  icon: FingerprintIcon,
  title: 'Biometric Access Systems',
  description: 'Enhance security with fingerprint or facial recognition technology, providing high-assurance identity verification for your most sensitive areas.',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}];
const managementFeatures = [{
  icon: CloudIcon,
  title: 'Cloud Management',
  desc: 'Manage access permissions and users from any web browser instantly.'
}, {
  icon: HistoryIcon,
  title: 'Audit Trails',
  desc: 'Detailed logs of every entry and exit, searchable by user or location.'
}, {
  icon: ClockIcon,
  title: 'Time-based Access',
  desc: 'Set specific schedules for when employees or contractors can enter.'
}, {
  icon: ShieldAlertIcon,
  title: 'Instant Revocation',
  desc: 'Immediately disable access for lost cards or terminated employees.'
}];
const integrationPoints = [{
  icon: VideoIcon,
  title: 'Video Integration',
  desc: 'Link access events with video footage for visual verification of every entry.'
}, {
  icon: BellIcon,
  title: 'Alarm Systems',
  desc: 'Automatically arm or disarm security systems based on first-in/last-out access.'
}, {
  icon: SmartphoneIcon,
  title: 'Mobile Alerts',
  desc: 'Receive push notifications for unauthorized entry attempts or forced doors.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Audit',
  desc: 'Evaluation of entry points and security requirements.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Custom hardware and software configuration for your site.'
}, {
  icon: WrenchIcon,
  title: 'Install',
  desc: 'Seamless installation with minimal disruption to operations.'
}, {
  icon: HeadphonesIcon,
  title: 'Support',
  desc: '24/7 support and regular system health checks.'
}];
export function AccessControlPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-658">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-659">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-660"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-661"></div>
        <div className="absolute top-0 left-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-662"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-663">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-664">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-665">
              <LockIcon className="w-4 h-4 mr-2 text-indigo-400" data-id="element-666" />
              Access Control
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-667">
              Modern
              <br className="hidden md:block" data-id="element-668" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-669">
                Access Control
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-670">
              From keycards to mobile and biometric access, we provide scalable
              access control systems to secure your facilities and protect your
              people.
            </p>
            <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-671">
              Manage Access
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-672" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-50" data-id="element-673">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-674">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-675">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-676">
              Smart Access Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-677">
              Eliminate the risk of lost physical keys. Our access control
              systems give you granular control over who can enter your building
              and when.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-678">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-679">
                  <div className={`w-14 h-14 ${service.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-680">
                    <Icon className={`w-7 h-7 ${service.color}`} data-id="element-681" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-682">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-683">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Management Features */}
      <section className="py-24 bg-white" data-id="element-684">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-685">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-686">
            <div className="order-2 lg:order-1" data-id="element-687">
              <img src="https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&q=80&w=800" alt="Cloud Access Management" className="rounded-3xl shadow-2xl" data-id="element-688" />
            </div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="order-1 lg:order-2" data-id="element-689">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-690">
                Centralized Cloud Management
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-691">
                Manage your entire facility's security from a single dashboard.
                Whether you have one door or one hundred across multiple
                locations, our cloud platform makes administration simple.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-692">
                {managementFeatures.map((feature, i) => <div key={i} className="bg-slate-50 p-6 rounded-xl border border-slate-100" data-id="element-693">
                    <feature.icon className="w-8 h-8 text-primary mb-4" data-id="element-694" />
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-695">
                      {feature.title}
                    </h4>
                    <p className="text-sm text-slate-500" data-id="element-696">{feature.desc}</p>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Visitor Management */}
      <section className="py-24 bg-slate-50" data-id="element-697">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-698">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-699">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-700">
              Modern Visitor Management
            </h2>
            <p className="text-lg text-slate-600" data-id="element-701">
              Create a welcoming, secure experience for guests, contractors, and
              interviewees without compromising facility security.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-702">
            {[{
            icon: QrCodeIcon,
            title: 'Temporary QR Codes',
            desc: 'Email guests a QR code that only works for specific doors during their scheduled visit time.'
          }, {
            icon: UserPlusIcon,
            title: 'Self-Service Kiosks',
            desc: 'Allow visitors to check themselves in via an iPad at the front desk.'
          }, {
            icon: FileTextIcon,
            title: 'Digital Sign-in Logs',
            desc: 'Eliminate paper logbooks. Automatically track exactly who is in the building at any time.'
          }, {
            icon: BellRingIcon,
            title: 'Host Notifications',
            desc: 'Automatically text or Slack employees when their guest arrives in the lobby.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm text-center" data-id="element-703">
                <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-704">
                  <feature.icon className="w-7 h-7 text-indigo-600" data-id="element-705" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-706">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-707">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-708">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-709">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-710">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-711">
              Seamless Security Integration
            </h2>
            <p className="text-slate-400" data-id="element-712">
              Access control is most powerful when it works in harmony with your
              other security layers.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-713">
            {integrationPoints.map((point, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 p-8 rounded-2xl text-center" data-id="element-714">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-715">
                  <point.icon className="w-8 h-8 text-primary" data-id="element-716" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-717">{point.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-718">
                  {point.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Compliance & Security Standards */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden border-t border-white/10" data-id="element-719">
        <div className="absolute top-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px]" data-id="element-720"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-721">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-722">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-723">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-724">
                Built for Compliance
              </h2>
              <p className="text-slate-400 text-lg mb-8" data-id="element-725">
                Physical security is a critical component of major regulatory
                frameworks. Our access control systems provide the audit trails
                and restrictions required to pass your next audit.
              </p>
              <div className="space-y-4" data-id="element-726">
                {[{
                title: 'SOC 2',
                desc: 'Prove physical access to servers and data is restricted and logged.'
              }, {
                title: 'HIPAA',
                desc: 'Secure rooms containing PHI and track employee entry.'
              }, {
                title: 'PCI-DSS',
                desc: 'Restrict access to areas where cardholder data is processed.'
              }].map((item, i) => <div key={i} className="flex items-start gap-4 bg-white/5 p-4 rounded-xl border border-white/10" data-id="element-727">
                    <ShieldCheckIcon className="w-6 h-6 text-indigo-400 shrink-0" data-id="element-728" />
                    <div data-id="element-729">
                      <h4 className="font-bold text-white" data-id="element-730">{item.title}</h4>
                      <p className="text-sm text-slate-400" data-id="element-731">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="relative" data-id="element-732">
              <div className="bg-white/5 backdrop-blur-md border border-white/10 p-10 rounded-3xl text-center" data-id="element-733">
                <HistoryIcon className="w-16 h-16 text-indigo-400 mx-auto mb-6" data-id="element-734" />
                <h3 className="text-2xl font-bold mb-4" data-id="element-735">
                  Indisputable Audit Trails
                </h3>
                <p className="text-slate-400 mb-8" data-id="element-736">
                  Generate comprehensive reports showing exactly who accessed
                  which doors, at what time, and any denied entry attempts.
                </p>
                <button onClick={() => onNavigate('contact')} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-xl transition-colors" data-id="element-737">
                  Discuss Compliance Needs
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-738">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-739">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-740">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-741">
              Our Implementation Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-742">
              We handle everything from site assessment to final staff training.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-743">
            {processSteps.map((step, i) => <div key={i} className="relative text-center" data-id="element-744">
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-745">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-746" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-747">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-748">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection data-id="element-749" />

      {/* FAQ Section */}
      <section className="py-24 bg-slate-50" data-id="element-750">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-751">
          <div className="text-center mb-16" data-id="element-752">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-753">
              <HelpCircleIcon className="w-8 h-8 text-indigo-600" data-id="element-754" />
              Access Control FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-755">
              Common questions about securing your facility.
            </p>
          </div>
          <div className="space-y-4" data-id="element-756">
            {[{
            q: 'What happens during a power outage?',
            a: "Our systems are installed with battery backups that keep the card readers and electronic locks functioning during a power loss. If the battery depletes, doors can be configured to 'fail safe' (unlock for easy exit) or 'fail secure' (remain locked from the outside)."
          }, {
            q: 'Can employees use their smartphones to unlock doors?',
            a: 'Yes! We offer mobile credentialing via Bluetooth or NFC. Employees simply wave their phone near the reader to unlock the door, eliminating the need for physical plastic badges.'
          }, {
            q: 'How hard is it to add or remove users?',
            a: "With cloud management, it takes seconds. You can instantly revoke access from a terminated employee's badge or phone from any web browser, ensuring they cannot re-enter the building."
          }, {
            q: 'Can it integrate with our HR system?',
            a: 'Yes. Many of our cloud-based systems integrate with platforms like Workday, BambooHR, or Active Directory to automatically grant or revoke access based on employee status.'
          }, {
            q: 'What if someone loses their keycard?',
            a: 'Unlike physical keys where you have to re-key the entire building, a lost keycard can be instantly deactivated in the system, rendering it useless if found by a bad actor.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-757">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-758">
                  <span className="font-bold text-slate-900" data-id="element-759">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-760">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-761" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-762">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-763">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-764">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-765">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-766"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-767">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-768">
            Take control of your facility's security.
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-769">
            Let our experts design an access control system that fits your
            operational flow and security requirements.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl inline-flex items-center" data-id="element-770">
            Get a Free Quote
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-771" />
          </button>
        </div>
      </section>
    </div>;
}